import { useState } from 'react';
import { Loader2, GitCompareArrows } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const KIND_LABEL = {
  counterpart_mismatch: 'Counterpart mismatch',
  push_conflict: 'Push conflict',
};

const KIND_STYLE = {
  counterpart_mismatch: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  push_conflict: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

function rowDetail(row) {
  if (row.kind === 'counterpart_mismatch') {
    return `${row.source_system}: ${row.ledger_counterpart_display} ≠ FA: ${row.fa_counterpart_display}`;
  }
  return `Pennylane invoice ${row.pennylane_invoice_display} already linked to transaction(s) ${row.conflicting_pennylane_transaction_ids.join(', ')}`;
}

export function ReconciliationDisagreementsPanel() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.reconciliationDisagreements();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <GitCompareArrows className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Reconciliation Disagreements</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only, no writes. Counterpart mismatch: the accounting ledger (Pennylane or Xero)
          shows a transaction reconciled to one invoice, but FA settled the same transaction
          against a different one. Push conflict: services/pennylane_push_retry.py's conflict
          guard refused to overwrite an existing different-counterpart Pennylane link. Neither
          case is auto-resolved here -- this only surfaces the disagreement for a human to
          decide.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button data-testid="reconciliation-disagreements-run" size="sm" variant="outline" onClick={run} disabled={loading}>
          {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Run
        </Button>

        {result && (
          <div className="text-xs space-y-2" data-testid="reconciliation-disagreements-result">
            <div className="text-muted-foreground">
              {result.counterpart_mismatch_count} counterpart mismatch(es), {result.push_conflict_count} push conflict(s)
              {(result.counterpart_mismatch_count + result.push_conflict_count) === 0 && ' -- none found.'}
            </div>
            {result.rows.length > 0 && (
              <table className="w-full text-xs border-collapse" data-testid="reconciliation-disagreements-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-1 pr-3">Kind</th>
                    <th className="py-1 pr-3">Transaction</th>
                    <th className="py-1 pr-3">Entity</th>
                    <th className="py-1 pr-3">Amount</th>
                    <th className="py-1 pr-3">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row, i) => (
                    <tr key={`${row.kind}-${row.transaction_id}-${i}`} className="border-b border-muted/50">
                      <td className="py-1 pr-3">
                        <Badge variant="secondary" className={cn('text-xs', KIND_STYLE[row.kind])}>
                          {KIND_LABEL[row.kind] || row.kind}
                        </Badge>
                      </td>
                      <td className="py-1 pr-3 font-mono">{row.transaction_id}</td>
                      <td className="py-1 pr-3">{row.entity_id}</td>
                      <td className="py-1 pr-3">{row.amount} {row.currency}</td>
                      <td className="py-1 pr-3">{rowDetail(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
